import _ from "lodash";
import Aqumen from "@aqumen/sdk";
import React, {useState} from "react";
import {useIntl} from "react-intl";
import {useDispatch, useSelector} from "react-redux";

import {blurOnEnter} from "@/library/utility/blur_on_enter.js";
import {CONFIRM_DESTRUCTIVE_TIMEOUT_MS} from "@/constants.js";
import {setAppSetting} from "@/data/preferences/set_app_setting.js";
import {UserOrganizationTile} from "./user_organization_tile.jsx";

function emptyUser() {
  return {email: "", fullName: ""};
}

const MIN_NEW_USER_PROPERTY_LENGTH = {
  additionalMessage: 0,
  email: 3,
  fullName: 2
};

export function NewUserTile(props) {
  const dispatch = useDispatch();
  const intl = useIntl();

  const [additionalMessage, setAdditionalMessage] = useState("");
  const [blanks, setBlanks] = useState({email: true, fullName: true});
  const [confirming, setConfirming] = useState(false);
  const [invalids, setInvalids] = useState({email: false, fullName: false});
  const [user, setUser] = useState(emptyUser());
  const [working, setWorking] = useState(false);

  const session = useSelector(s => s.accessControlSession);

  const handleChangeUser = (property) => (ev) => {
    setUser(Object.assign({}, user, {[property]: ev.target.value}));
    if (ev.target.value.length > 0
        && ev.target.value.trim().length < MIN_NEW_USER_PROPERTY_LENGTH[property]) {
      setInvalids({...invalids, [property]: true});
    } else if (Object.hasOwn(blanks, property) && Aqumen.Utility.isBlank(ev.target.value)) {
      setBlanks({...blanks, [property]: true});
      setInvalids({...invalids, [property]: false});
    } else {
      if (Object.hasOwn(blanks, property)) {
        setBlanks({...blanks, [property]: false});
      }
      setInvalids({...invalids, [property]: false});
    }
  };
  const handleChangeAdditionalMessage = (ev) => {
    setAdditionalMessage(ev.target.value);
  }
  const handleChangeOrganization = (ev) => {
    const organization = (ev.target.value) ? {id: ev.target.value}  : null;
    setUser(Object.assign({}, user, {organization}));
  }

  const handleCreate = async () => {
    setWorking(true);
    try {
      await Aqumen.User.create(session, Aqumen.Utility.create("User", session, {
        ...user,
        // FIXME remove once full access control finished
        roles: await Aqumen.Role.all(session, {id: true}, {roles: {identifier: "Restricted"}})
      }));
      setUser(emptyUser());
      dispatch(setAppSetting("users.showNewCardOnce", false));
    } finally {
      setWorking(false);
    }
  };

  const handleCreateAndSend = async () => {
    if (!confirming) {
      setConfirming(true);
      setTimeout(() => setConfirming(false), CONFIRM_DESTRUCTIVE_TIMEOUT_MS);
      return;
    }
    setConfirming(false);
    setWorking(true);
    try {
      const creating = Aqumen.Utility.create("User", session, user);
      const created = await Aqumen.User.create(session, creating);
      setUser(emptyUser());
      setAdditionalMessage("");
      const reloadAndSend = [
        await fetch(session.url + "/api/v2/webauthn/registration/send", {
          method: "POST",
          credentials: "include",
          headers: {
            "API-Key": session.key,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            id: created.id,
            additionalMessage: (additionalMessage.length === 0) ? null : additionalMessage
          })
        })
      ];
      await Promise.all(reloadAndSend);
    } catch (e) {
      setConfirming(true);
      setTimeout(() => setConfirming(false), CONFIRM_DESTRUCTIVE_TIMEOUT_MS);
    } finally {
      setWorking(false);
    }
  };

  const allDisabled = working
    || Object.values(blanks).some(v => v)
    || Object.values(invalids).some(v => v);
  const noEmailDisabled = additionalMessage.length > 0;
  const i18nPrefix = "users.new";
  let className = Object.keys(invalids).reduce((r, k) => {
    return (invalids[k]) ? (r + " invalid-" + _.kebabCase(k)) : r;
  }, "user-card new focusable-item");
  if (working) {
    className += " working";
  }
  if (confirming) {
    className += " confirming";
  }
  return (
    <div className={className}>
      <div className="user-card-inputs">
        <label className="user-full-name">
          <span className="label-text">
            {intl.formatMessage({id: "users.new.fullName.label"})}
          </span>
          <input className="user-full-name"
                 placeholder={intl.formatMessage({id: "users.new.fullName.placeholder"})}
                 type="text"
                 disabled={working}
                 onChange={handleChangeUser("fullName")}
                 onKeyUp={blurOnEnter}
                 value={user.fullName}/>
        </label>
        <label className="user-email">
          <span className="label-text">
            {intl.formatMessage({id: "users.new.email.label"})}
          </span>
          <input className="user-email"
                 placeholder={intl.formatMessage({id: "users.new.email.placeholder"})}
                 type="email"
                 disabled={working}
                 onChange={handleChangeUser("email")}
                 onKeyUp={blurOnEnter}
                 value={user.email}/>
        </label>
        <UserOrganizationTile user={user}
                              disabled={working}
                              i18nPrefix={i18nPrefix}
                              onChange={handleChangeOrganization}/>

        <label className="user-additional-message">
          <span className="label-text">
            {intl.formatMessage({id: "users.new.additionalMessage.label"})}
          </span>
          <textarea className="user-additional-message"
                    placeholder={intl.formatMessage({id: "users.new.additionalMessage.placeholder"})}
                    disabled={working}
                    onChange={handleChangeAdditionalMessage}
                    value={additionalMessage}/>
        </label>
      </div>

      <div className="user-card-meta">
        <div className="user-card-actions">
          <div className="user-card-action">
            <button className="user-create-and-send"
                    disabled={allDisabled}
                    onClick={handleCreateAndSend}>
              {!confirming && intl.formatMessage({id: "users.new.createAndSend.button"})}
              {confirming && intl.formatMessage({id: "users.new.createAndSend.confirm.button"})}
            </button>
          </div>
          <div className="user-card-action">
            <button className="user-create" disabled={allDisabled || noEmailDisabled} onClick={handleCreate}>
              {intl.formatMessage({id: "users.new.create.button"})}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
